<template>
  <section class="cart confirm">
    <QuotesSlider class="confirm__quotes" />
    <router-link :to="{ name: 'shop' }" class="btn btn--tertiary cart__shop-link"
      >Back to shop</router-link
    >
    <div class="cart__shop">
      <div class="cart__main">
        <ShoppingCartList />
      </div>
      <div class="cart__aside">
        <div class="confirm__details-wrapper">
          <h4 class="confirm__title">Confirm Details</h4>
          <UserEntriesList @create-account-clicked="handleModal" />
        </div>
        <div class="confirm__actions-wrapper">
          <vue-loaders v-if="isLoading" name="line-scale" color="#ef7d22"></vue-loaders>
          <div v-else class="cart__actions">
            <div class="cart__tos">
              By continuing, you agree to INE’s
              <a
                href="https://ine.com/terms-of-service"
                :class="['cart__tos--link', isLoading && 'btn--loading']"
                target="_blank"
                >terms of service</a
              >.
            </div>
            <button @click="fetchPlaceOrderRecaptcha()" class="btn btn--primary">
              Place Order
            </button>
          </div>
        </div>
      </div>
    </div>
    <SmallPrints flow-styles />
    <CaptchaBlock
      ref="invisibleRecaptcha"
      keyType="subscriptions"
      @captcha-verify-action="submitOrder"
    />
  </section>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import QuotesSlider from '@/components/flow/QuotesSlider.vue'
import SmallPrints from '@/components/flow/small-prints/SmallPrints.vue'
import UserEntriesList from '@/components/user-details/UserEntriesList.vue'
import ShoppingCartList from '@/components/cart/ShoppingCartList.vue'
import CaptchaBlock from '@/components/shared/CaptchaBlock.vue'
import router from '@/router'
import { eventsConstants } from '@/helpers/eventsList'
import { mixpanelEvent } from '@/helpers/mixins'

export default {
  name: 'Confirm',
  mixins: [mixpanelEvent],
  components: {
    QuotesSlider,
    UserEntriesList,
    ShoppingCartList,
    CaptchaBlock,
    SmallPrints,
  },
  data() {
    return {
      risk: {},
      threeDSecure: {},
      challengeToken: '',
    }
  },
  computed: {
    ...mapState({
      availablePlans: state => state.products.availablePlans,
      dataLayer: state => state.events.dataLayer,
      isAuthenticated: state => state.auth.isAuthenticated,
      isEditModalShown: state => state.app.isEditModalShown,
      isLoading: state => state.app.isComponentLoading,
      isModalShown: state => state.app.isNewAccountModalShown,
      paymentEntries: state => state.subscriptions.paymentEntries,
      paymentMethod: state => state.subscriptions.paymentMethod,
      shoppingCart: state => state.shoppingCart.shoppingCart,
      userProfile: state => state.auth.userProfile,
      utmParams: state => state.events.utmParams,
      isIn3dsFlow: state => state.subscriptions.isIn3dsFlow,
    }),
    ...mapGetters({
      selectedPlan: 'shoppingCart/selectedPlan',
      couponStr: 'coupons/getCoupon',
      taxesStr: 'priceDetails/getTaxesStr',
      totalStr: 'priceDetails/getTotalStr',
      recurlyToken: 'recurly/getRecurlyToken',
      actionTokenId: 'recurly/getRecurlyTokenActionIdn',
    }),
  },
  watch: {
    shoppingCart: {
      immediate: true,
      handler(items) {
        if (items.length === 0) {
          this.$router.push({ name: 'shop' })
        }
      },
    },
    isIn3dsFlow(state) {
      if (state) {
        this.start3dsFlow()
      }
    },
  },
  methods: {
    ...mapMutations({
      setIsModalShown: 'app/IS_NEW_ACCOUNT_MODAL_SHOWN_UPDATED',
      setIsEditModalShown: 'app/IS_EDIT_MODAL_SHOWN_UPDATED',
    }),
    handleModal() {
      this.setIsModalShown(!this.isModalShown)
      this.setIsEditModalShown(true)
    },
    fetchPlaceOrderRecaptcha() {
      this.$refs.invisibleRecaptcha.execute()
    },
    async submitOrder({ recaptcha }) {
      this.$store.dispatch('app/setIsComponentLoading', true)
      try {
        const response = await this.$store.dispatch('subscriptions/submitPaymentInfo', {
          captchaToken: recaptcha,
          challengeToken: this.challengeToken,
        })
        if (response) {
          this.$store.dispatch('auth/createUserAccount', {
            ...this.userProfile,
            user_id: response.user_id,
          })
          if (response.error) return
          await this.sendMixpanelEvent(eventsConstants.CHECKOUT_COMPLETED, {
            checkout_total: this.totalStr,
            payment_method: this.paymentMethod,
            plan_code: this.selectedPlan?.merchant_id ?? '',
            coupon: this.couponStr,
            has_3ds_flow: !!this.challengeToken,
            ...this.utmParams,
          })
          await this.$store.dispatch('subscriptions/setPartnerStackFlow', true)
          const transaction = {
            id: response.invoice_number,
            affiliation: 'checkout.ine.com',
            tax: this.taxesStr,
            revenue: this.totalStr,
          }
          this.gaPurchase({
            transaction_id: transaction.id,
          })
          this.$ga.ecommerce.addTransaction(transaction)
          this.$ga.ecommerce.send()
          this.challengeToken = ''
          router.push({ name: 'complete' })
        }
      } catch (error) {
        this.$store.dispatch('app/setError', error)
      }
    },
    start3dsFlow() {
      const { actionTokenId } = this
      this.risk = window.recurly.Risk()
      this.threeDSecure = this.risk.ThreeDSecure({
        actionTokenId,
      })
      this.threeDSecure.on('token', token => this.threeDSecureResolved(token))
      this.threeDSecure.on('error', error => {
        this.$store.dispatch(
          'app/setError',
          error && error.message
            ? error.message
            : 'We were unable to verify your order through 3D-Secure. Please contact your bank.'
        )
        this.threeDSecure.remove()
        this.$store.commit('subscriptions/THREE_DS_FLOW_UPDATED', false)
      })
      this.threeDSecure.attach(document.querySelector('#recurly-3ds'))
    },
    async threeDSecureResolved(token) {
      const { id } = token
      this.challengeToken = id
      this.$store.dispatch('subscriptions/resolve3dsFlow', id)
      this.threeDSecure.remove()
      await this.fetchPlaceOrderRecaptcha()
    },
  },
}
</script>

<style lang="scss">
.confirm {
  max-width: 1200px;
  &__title {
    font-family: var(--font-regular);
    font-size: 24px;
    line-height: 32px;
    color: var(--white);
    margin: 0;
  }
  .cart {
    &__aside {
      display: flex;
      justify-content: space-between;
      flex-flow: column;
    }
    &__shop {
      margin: 45px 0 160px 0;
      @include tablet {
        margin: 25px 0 0 0;
      }
    }
    &__main {
      padding: 20px;
      @include mobile {
        padding: 40px;
      }
    }
    &__actions {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-content: center;
      flex-flow: column;
      .btn {
        height: 36px;
        font-size: rem(14px);
      }
      @include tablet {
        flex-flow: row;
        .btn {
          height: 48px;
          font-size: 1rem;
        }
      }
    }
    &__tos {
      margin: auto 0;
      padding: 0;
      padding-bottom: 20px;
      font-size: rem(12px);
      line-height: 14px;
      @include tablet {
        padding-bottom: 0;
      }
    }
  }
  &__actions-wrapper {
    display: flex;
    justify-content: center;
  }
  &__quotes {
    margin-bottom: 30px;
  }
}
</style>
